/**
 * Custom hook for Enhanced Dashboard data management
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  getAssessment,
  getPersonalAssessment,
  getBusinessProfile,
  getWealthGap,
  saveWealthGap,
  autoCalculateProfitGap,
  autoCalculateValueGap,
  getTaskStats,
  getValuationHistory,
  getProfitGap,
  getValueGap,
  getValuation
} from '../services/api';
import { saveUserTimezone } from '../utils/dateUtils';
import { DEFAULT_WEALTH_GAP_VALUES } from '../utils/enhancedDashboardConstants';
import logger from '../utils/logger';

export const useEnhancedDashboardData = (isAdvisor, currentBusinessId) => {
  const navigate = useNavigate();

  // Core data state
  const [latestValuation, setLatestValuation] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [personalAssessment, setPersonalAssessment] = useState(null);
  const [wealthGap, setWealthGap] = useState(null);
  const [profitGap, setProfitGap] = useState(null);
  const [valueGap, setValueGap] = useState(null);
  const [taskStats, setTaskStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Wealth Gap Calculator State
  const [showWealthGapCalculator, setShowWealthGapCalculator] = useState(false);
  const [wealthGapMethod, setWealthGapMethod] = useState('single_number');
  const [wealthGoalAmount, setWealthGoalAmount] = useState('');
  const [monthlyCashNeed, setMonthlyCashNeed] = useState('');
  const [yearsOfIncome, setYearsOfIncome] = useState(DEFAULT_WEALTH_GAP_VALUES.YEARS_OF_INCOME);
  const [inflationRate, setInflationRate] = useState(DEFAULT_WEALTH_GAP_VALUES.INFLATION_RATE);
  const [returnRate, setReturnRate] = useState(DEFAULT_WEALTH_GAP_VALUES.RETURN_RATE);
  const [currentNetWorth, setCurrentNetWorth] = useState('');
  const [liquidAssets, setLiquidAssets] = useState('');
  const [retirementAccounts, setRetirementAccounts] = useState('');
  const [realEstateEquity, setRealEstateEquity] = useState('');
  const [otherInvestments, setOtherInvestments] = useState('');
  const [totalLiabilities, setTotalLiabilities] = useState('');
  const [savingWealthGap, setSavingWealthGap] = useState(false);

  // New enhanced fields
  const [outsideIncomeMonthly, setOutsideIncomeMonthly] = useState('');
  const [outsideIncomeAnnual, setOutsideIncomeAnnual] = useState('');
  const [outsideIncomeAnnualIncrease, setOutsideIncomeAnnualIncrease] = useState('');
  const [currentAge, setCurrentAge] = useState('');
  const [retirementAge, setRetirementAge] = useState('');
  const [lifeExpectancy, setLifeExpectancy] = useState('');
  const [taxRateOnSale, setTaxRateOnSale] = useState('');
  const [ownershipPercentage, setOwnershipPercentage] = useState('');
  const [desiredSalePrice, setDesiredSalePrice] = useState('');
  const [businessGrowthRate, setBusinessGrowthRate] = useState('');
  const [exitYear, setExitYear] = useState('');
  const [inheritanceAmount, setInheritanceAmount] = useState('');
  const [leisureItemsAmount, setLeisureItemsAmount] = useState('');
  const [lifestyleChangePercentage, setLifestyleChangePercentage] = useState('');
  const [spendDecreaseAfterRetirement, setSpendDecreaseAfterRetirement] = useState('');

  // Individual return rates for each asset
  const [liquidAssetsReturnRate, setLiquidAssetsReturnRate] = useState('');
  const [retirementAccountsReturnRate, setRetirementAccountsReturnRate] = useState('');
  const [realEstateReturnRate, setRealEstateReturnRate] = useState('');
  const [otherInvestmentsReturnRate, setOtherInvestmentsReturnRate] = useState('');

  // Gap Detail Modal State
  const [showWealthGapDetails, setShowWealthGapDetails] = useState(false);
  const [showProfitGapDetails, setShowProfitGapDetails] = useState(false);
  const [showValueGapDetails, setShowValueGapDetails] = useState(false);
  const [showGapModal, setShowGapModal] = useState(false);

  // Fetch all dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Check Supabase session first
      const { data: { session } } = await supabase.auth.getSession();
      let token = session?.access_token;

      // Fall back to Flask JWT token
      if (!token) {
        token = localStorage.getItem('token');
      }

      if (!token) {
        logger.warn('No authentication token found, redirecting to login');
        navigate('/login');
        return;
      }

      // Fetch all data in parallel for maximum speed
      const [
        profileResult,
        valuationResponse,
        assessmentResult,
        personalAssessmentResult,
        wealthGapResult,
        profitGapResponse,
        valueGapResponse,
        taskStatsResponse
      ] = await Promise.allSettled([
        getBusinessProfile(),
        getValuationHistory(),
        getAssessment(),
        getPersonalAssessment(),
        getWealthGap(),
        getProfitGap(),
        getValueGap(),
        getTaskStats()
      ]);

      // Process profile
      if (profileResult.status === 'fulfilled' && profileResult.value.success && profileResult.value.data.profile) {
        const profile = profileResult.value.data.profile;
        setBusinessProfile(profile);

        // Do NOT save business owner's timezone to localStorage
        // This would override the logged-in user's (advisor's) timezone preference
      }

      // Process valuation
      if (valuationResponse.status === 'fulfilled' && valuationResponse.value.success) {
        const valuations = valuationResponse.value.data.valuations || [];
        if (valuations.length > 0) {
          let latestVal = valuations[0];

          // Map fields immediately
          if (latestVal.low_range && latestVal.high_range) {
            latestVal.valuation_low = latestVal.low_range;
            latestVal.valuation_high = latestVal.high_range;
          }

          // Fallback to business profile for revenue/EBITDA if not in valuation
          if (profileResult.status === 'fulfilled' && profileResult.value.success && profileResult.value.data.profile) {
            const profileRevenue = parseFloat(profileResult.value.data.profile.revenue);
            const profileEbitda = parseFloat(profileResult.value.data.profile.ebitda);

            if (!latestVal.revenue && !isNaN(profileRevenue)) {
              latestVal.revenue = profileRevenue;
            }
            if (!latestVal.ebitda && !isNaN(profileEbitda)) {
              latestVal.ebitda = profileEbitda;
            }
          }

          setLatestValuation(latestVal);

          // Fetch additional details asynchronously if needed (non-blocking)
          // Fetch for: missing ranges, OR missing revenue/EBITDA for any type
          if (!latestVal.low_range || !latestVal.high_range || !latestVal.revenue || !latestVal.ebitda) {
            getValuation(latestVal.id).then(detailResult => {
              if (detailResult.success) {
                const updatedVal = { ...latestVal };
                if (detailResult.data.low_range) {
                  updatedVal.low_range = detailResult.data.low_range;
                  updatedVal.high_range = detailResult.data.high_range;
                  updatedVal.valuation_low = detailResult.data.low_range;
                  updatedVal.valuation_high = detailResult.data.high_range;
                }
                if (detailResult.data.input_data) {
                  // Parse revenue and ebitda, converting to numbers
                  const revenue = parseFloat(detailResult.data.input_data.revenue);
                  const ebitda = parseFloat(detailResult.data.input_data.ebitda);

                  if (!isNaN(revenue)) {
                    updatedVal.revenue = updatedVal.revenue || revenue;
                  }
                  if (!isNaN(ebitda)) {
                    updatedVal.ebitda = updatedVal.ebitda || ebitda;
                  }
                }
                setLatestValuation(updatedVal);
              }
            }).catch(err => logger.error('Error fetching valuation details', err));
          }
        }
      }

      // Process assessment
      if (assessmentResult.status === 'fulfilled' && assessmentResult.value.success && assessmentResult.value.data) {
        setAssessment(assessmentResult.value.data);
      }

      // Process personal assessment
      if (personalAssessmentResult.status === 'fulfilled' && personalAssessmentResult.value.success && personalAssessmentResult.value.data) {
        setPersonalAssessment(personalAssessmentResult.value.data);
      }

      // Process wealth gap
      if (wealthGapResult.status === 'fulfilled' && wealthGapResult.value.success && wealthGapResult.value.data.wealth_gap) {
        setWealthGap(wealthGapResult.value.data.wealth_gap);
        populateWealthGapForm(wealthGapResult.value.data.wealth_gap);
      }

      // Process profit gap
      if (profitGapResponse.status === 'fulfilled' && profitGapResponse.value?.success && profitGapResponse.value?.data.profit_gap) {
        setProfitGap(profitGapResponse.value.data.profit_gap);
      }

      // Process value gap
      if (valueGapResponse.status === 'fulfilled' && valueGapResponse.value?.success && valueGapResponse.value?.data.value_gap) {
        setValueGap(valueGapResponse.value.data.value_gap);
      }

      // Process task stats
      if (taskStatsResponse.status === 'fulfilled' && taskStatsResponse.value?.success && taskStatsResponse.value?.data.stats) {
        setTaskStats(taskStatsResponse.value.data.stats);
      }

    } catch (error) {
      logger.error('Error fetching dashboard data', error);
    } finally {
      setLoading(false);
    }
  };

  // Populate wealth gap form with existing data
  const populateWealthGapForm = (data) => {
    setWealthGapMethod(data.wealth_goal_method || 'single_number');
    setWealthGoalAmount(data.wealth_goal_amount || '');
    setMonthlyCashNeed(data.monthly_cash_need || '');
    setYearsOfIncome(data.years_of_income || DEFAULT_WEALTH_GAP_VALUES.YEARS_OF_INCOME);
    setInflationRate(data.annual_inflation_rate || DEFAULT_WEALTH_GAP_VALUES.INFLATION_RATE);
    setReturnRate(data.annual_return_rate || DEFAULT_WEALTH_GAP_VALUES.RETURN_RATE);
    setCurrentNetWorth(data.current_net_worth || '');
    setLiquidAssets(data.liquid_assets || '');
    setRetirementAccounts(data.retirement_accounts || '');
    setRealEstateEquity(data.real_estate_equity || '');
    setOtherInvestments(data.other_investments || '');
    setTotalLiabilities(data.total_liabilities || '');

    // Populate new enhanced fields
    setOutsideIncomeMonthly(data.outside_income_monthly || '');
    setOutsideIncomeAnnual(data.outside_income_annual || '');
    setOutsideIncomeAnnualIncrease(data.outside_income_annual_increase || '');
    setCurrentAge(data.current_age || '');
    setRetirementAge(data.retirement_age || '');
    setLifeExpectancy(data.life_expectancy || '');
    setTaxRateOnSale(data.tax_rate_on_sale || '');
    setOwnershipPercentage(data.ownership_percentage || '');
    setDesiredSalePrice(data.desired_sale_price || '');
    setBusinessGrowthRate(data.business_growth_rate || '');
    setExitYear(data.exit_year || '');
    setInheritanceAmount(data.inheritance_amount || '');
    setLeisureItemsAmount(data.leisure_items_amount || '');
    setLifestyleChangePercentage(data.lifestyle_change_percentage || '');
    setSpendDecreaseAfterRetirement(data.spend_decrease_after_retirement || '');

    // Populate individual return rates
    setLiquidAssetsReturnRate(data.liquid_assets_return_rate || '3');
    setRetirementAccountsReturnRate(data.retirement_accounts_return_rate || '7');
    setRealEstateReturnRate(data.real_estate_return_rate || '4');
    setOtherInvestmentsReturnRate(data.other_investments_return_rate || '6');
  };

  // Save wealth gap data
  const handleSaveWealthGap = async (dataFromModal) => {
    setSavingWealthGap(true);
    try {
      // Use data from modal if provided, otherwise build from hook state
      const wealthGapData = dataFromModal || {
        wealth_goal_method: wealthGapMethod,
        wealth_goal_amount: parseFloat(wealthGoalAmount) || 0,
        monthly_cash_need: parseFloat(monthlyCashNeed) || 0,
        years_of_income: parseInt(yearsOfIncome) || 20,
        annual_inflation_rate: parseFloat(inflationRate) || 3.0,
        annual_return_rate: parseFloat(returnRate) || 7.0,
        current_net_worth: parseFloat(currentNetWorth) || 0,
        liquid_assets: parseFloat(liquidAssets) || 0,
        retirement_accounts: parseFloat(retirementAccounts) || 0,
        real_estate_equity: parseFloat(realEstateEquity) || 0,
        other_investments: parseFloat(otherInvestments) || 0,
        total_liabilities: parseFloat(totalLiabilities) || 0,
        outside_income_monthly: parseFloat(outsideIncomeMonthly) || 0,
        outside_income_annual: parseFloat(outsideIncomeAnnual) || 0,
        outside_income_annual_increase: parseFloat(outsideIncomeAnnualIncrease) || 0,
        current_age: parseInt(currentAge) || 50,
        retirement_age: parseInt(retirementAge) || 65,
        life_expectancy: parseInt(lifeExpectancy) || 100,
        tax_rate_on_sale: parseFloat(taxRateOnSale) || 42.0,
        ownership_percentage: parseFloat(ownershipPercentage) || 100.0,
        desired_sale_price: parseFloat(desiredSalePrice) || 0,
        business_growth_rate: parseFloat(businessGrowthRate) || 0,
        exit_year: parseInt(exitYear) || new Date().getFullYear(),
        inheritance_amount: parseFloat(inheritanceAmount) || 0,
        leisure_items_amount: parseFloat(leisureItemsAmount) || 0,
        lifestyle_change_percentage: parseFloat(lifestyleChangePercentage) || 0,
        spend_decrease_after_retirement: parseFloat(spendDecreaseAfterRetirement) || 1.0,
        liquid_assets_return_rate: parseFloat(liquidAssetsReturnRate) || 3.0,
        retirement_accounts_return_rate: parseFloat(retirementAccountsReturnRate) || 7.0,
        real_estate_return_rate: parseFloat(realEstateReturnRate) || 4.0,
        other_investments_return_rate: parseFloat(otherInvestmentsReturnRate) || 6.0
      };

      const result = await saveWealthGap(wealthGapData);
      if (result.success) {
        // Refetch wealth gap data to ensure we have the latest calculated values
        const fetchResult = await getWealthGap();
        if (fetchResult.success && fetchResult.data && fetchResult.data.wealth_gap) {
          setWealthGap(fetchResult.data.wealth_gap);
          populateWealthGapForm(fetchResult.data.wealth_gap);
        } else {
          // Fallback to saved data if refetch fails
          setWealthGap(result.data.wealth_gap);
          populateWealthGapForm(result.data.wealth_gap);
        }
        setShowWealthGapCalculator(false);
        alert('Wealth gap saved successfully!');
      } else {
        alert('Failed to save wealth gap: ' + result.error);
      }
    } catch (error) {
      logger.error('Error saving wealth gap', error);
      alert('Failed to save wealth gap');
    } finally {
      setSavingWealthGap(false);
    }
  };

  // Recalculate profit gap
  const handleRecalculateProfitGap = async () => {
    try {
      const result = await autoCalculateProfitGap();
      if (result.success) {
        await fetchDashboardData();
      } else {
        alert(result.error || 'Unable to recalculate profit gap. Please ensure you have a recent valuation with revenue and EBITDA data.');
      }
    } catch (error) {
      logger.error('Error recalculating profit gap', error);
      alert('Unable to recalculate profit gap. Please ensure you have a recent valuation with revenue and EBITDA data.');
    }
  };

  // Recalculate value gap
  const handleRecalculateValueGap = async () => {
    try {
      const result = await autoCalculateValueGap();
      if (result.success) {
        await fetchDashboardData();
      } else {
        alert(result.error || 'Unable to recalculate value gap. Please ensure you have a recent valuation and wealth gap data.');
      }
    } catch (error) {
      logger.error('Error recalculating value gap', error);
      alert('Unable to recalculate value gap. Please ensure you have a recent valuation and wealth gap data.');
    }
  };

  // Load data on mount and when business context changes
  useEffect(() => {
    // Wait for business context to be available
    if (!currentBusinessId) {
      // For advisors, redirect to advisor dashboard to select a business
      if (isAdvisor) {
        navigate('/advisor');
      }
      // For business owners, wait for BusinessContext to load their business
      return;
    }
    fetchDashboardData();
  }, [isAdvisor, currentBusinessId]);

  return {
    // Core data
    latestValuation,
    businessProfile,
    assessment,
    personalAssessment,
    wealthGap,
    profitGap,
    valueGap,
    taskStats,
    loading,

    // Wealth Gap Calculator state
    showWealthGapCalculator,
    setShowWealthGapCalculator,
    wealthGapMethod,
    setWealthGapMethod,
    wealthGoalAmount,
    setWealthGoalAmount,
    monthlyCashNeed,
    setMonthlyCashNeed,
    yearsOfIncome,
    setYearsOfIncome,
    inflationRate,
    setInflationRate,
    returnRate,
    setReturnRate,
    currentNetWorth,
    setCurrentNetWorth,
    liquidAssets,
    setLiquidAssets,
    retirementAccounts,
    setRetirementAccounts,
    realEstateEquity,
    setRealEstateEquity,
    otherInvestments,
    setOtherInvestments,
    totalLiabilities,
    setTotalLiabilities,
    savingWealthGap,

    // New enhanced fields
    outsideIncomeMonthly,
    setOutsideIncomeMonthly,
    outsideIncomeAnnual,
    setOutsideIncomeAnnual,
    outsideIncomeAnnualIncrease,
    setOutsideIncomeAnnualIncrease,
    currentAge,
    setCurrentAge,
    retirementAge,
    setRetirementAge,
    lifeExpectancy,
    setLifeExpectancy,
    taxRateOnSale,
    setTaxRateOnSale,
    ownershipPercentage,
    setOwnershipPercentage,
    desiredSalePrice,
    setDesiredSalePrice,
    businessGrowthRate,
    setBusinessGrowthRate,
    exitYear,
    setExitYear,
    inheritanceAmount,
    setInheritanceAmount,
    leisureItemsAmount,
    setLeisureItemsAmount,
    lifestyleChangePercentage,
    setLifestyleChangePercentage,
    spendDecreaseAfterRetirement,
    setSpendDecreaseAfterRetirement,

    // Individual return rates
    liquidAssetsReturnRate,
    setLiquidAssetsReturnRate,
    retirementAccountsReturnRate,
    setRetirementAccountsReturnRate,
    realEstateReturnRate,
    setRealEstateReturnRate,
    otherInvestmentsReturnRate,
    setOtherInvestmentsReturnRate,

    // Modal visibility
    showWealthGapDetails,
    setShowWealthGapDetails,
    showProfitGapDetails,
    setShowProfitGapDetails,
    showValueGapDetails,
    setShowValueGapDetails,
    showGapModal,
    setShowGapModal,

    // Handlers
    handleSaveWealthGap,
    handleRecalculateProfitGap,
    handleRecalculateValueGap,
    fetchDashboardData
  };
};
